import { useCallback, useEffect, useRef, useState } from 'react';

import braintree from 'braintree-web';
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import {
  ESdkEvents,
  EPaymentProvider,
  PaypalFinalizePaymentResponse,
  AppchargeErrorType,
} from '../../../../../contracts';
import { sendFEEvent } from '../../../../../eventUtil';
import { useAPI } from '../../../../../services/api.service';
import { useCheckout } from '../../../../../hooks/use-checkout';
import { ECheckoutStatus } from '../../../../../constants/enums';
import { usePaymentState } from '../../../hooks/use-payment-state';
import { useOrderPolling } from '../../../hooks/use-order-polling';
import { braintreeService } from '../../../../../services/braintree.service';
import { AddressElementRef } from '../../AddressElement/hooks/use-address-element-ref';
import { generateAppchargeErrorMessage } from '../../../../../utils/generate-appcharge-error-message';

export function useVenmoSubmit() {
  const { API } = useAPI();
  const isVenmoPaymentInProgress = useRef(false);
  const [venmoInstance, setVenmoInstance] = useState<braintree.Venmo>();
  const [braintreeDataCollector, setBraintreeDataCollector] = useState<braintree.DataCollector>();
  const { checkoutData, selectedPaymentMethod, getEventParams } = useCheckout();
  const { showBoundary } = useErrorBoundary();
  const { startOrderPolling } = useOrderPolling();
  const setIsApmLoading = usePaymentState((state) => state.setIsApmLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setCheckoutStatus = usePaymentState((state) => state.setCheckoutStatus);
  const setIsPaymentElementLoading = usePaymentState((state) => state.setIsPaymentElementLoading);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  const braintreePK = checkoutData?.pks.braintree;

  const initVenmo = useCallback(
    async (braintreePK: string) => {
      try {
        const venmoInstance = await braintreeService.createVenmoClient(braintreePK);
        setVenmoInstance(venmoInstance);
      } catch (error) {
        console.log({ error });
      }

      try {
        const dataCollector = await braintreeService.createBraintreeDataCollector(braintreePK);
        setBraintreeDataCollector(dataCollector);
      } catch (error) {
        console.log({ error });
      }
      setIsApmLoading(false);
      setIsPaymentElementLoading(false);
    },
    [setIsApmLoading, setIsPaymentElementLoading]
  );

  useEffect(() => {
    if (braintreePK) {
      initVenmo(braintreePK);
    }
  }, [initVenmo, braintreePK]);

  const handleSubmit = async (email: string, addressComponent?: AddressElementRef) => {
    if (isVenmoPaymentInProgress.current) {
      return;
    }
    addressComponent?.validate();
    const isZipCodeError = addressComponent && !addressComponent.isValid();
    if (isZipCodeError) {
      handleError();
      return;
    }
    setErrorMessage('');
    setIsPaymentLoading(true);
    isVenmoPaymentInProgress.current = true;
    venmoInstance
      ?.tokenize()
      .then(async (venmoTokenizePayload) => {
        if (venmoTokenizePayload) {
          try {
            const finalizeResponse: PaypalFinalizePaymentResponse = await API.finalizePayment({
              paymentProvider: selectedPaymentMethod?.provider,
              receipt_email: email,
              paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
              paymentMethodId: selectedPaymentMethod?.id,
              params: {
                paymentNonce: venmoTokenizePayload.nonce,
                deviceData: braintreeDataCollector?.deviceData,
              },
            });
            handleServerResponse(finalizeResponse);
          } catch (error: any) {
            showBoundary(error?.message);
            sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
              ...getEventParams(),
              reason: error.message,
              error: {
                isFatal: true,
                reason: error.message,
              },
            });
          }
        }
      })
      .catch((error) => handleError());
  };

  const handleServerResponse = (response: PaypalFinalizePaymentResponse) => {
    const status = response[EPaymentProvider.braintree]?.status;
    if (status === 'failed') {
      const message = response[EPaymentProvider.braintree]?.message;
      handleError(response.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: message,
      });
      setCheckoutStatus(ECheckoutStatus.INIT);
    } else if (status === 'succeeded') {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
      });
      startOrderPolling(response.orderId);
    }
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    setIsPaymentLoading(false);
    isVenmoPaymentInProgress.current = false;
    setErrorMessage(errorMessage);
  };

  return {
    handleSubmit,
  };
}
